import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import NotFound from "@/pages/NotFound";
import AppLayout from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import SharePage from "@/pages/SharePage";
import PitchDeck from "@/pages/PitchDeck";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Chat from "@/pages/Chat";
import Compare from "@/pages/Compare";
import Agents from "@/pages/Agents";
import Research from "@/pages/Research";
import Documents from "@/pages/Documents";
import WorkforceLayout from "@/pages/workforce/WorkforceLayout";
import MyWorkforce from "@/pages/workforce/MyWorkforce";
import CreateWorker from "@/pages/workforce/CreateWorker";
import WorkerWorkspace from "@/pages/workforce/WorkerWorkspace";
import WorkforceAnalytics from "@/pages/workforce/WorkforceAnalytics";
import Providers from "@/pages/workforce/Providers";
import Templates from "@/pages/workforce/Templates";
import Teams from "@/pages/workforce/Teams";
import DebateArena from "@/pages/workforce/DebateArena";
import Workflows from "@/pages/workforce/Workflows";
import WorkflowEditor from "@/pages/workforce/WorkflowEditor";
import Runs from "@/pages/workforce/Runs";
import RunDetail from "@/pages/workforce/RunDetail";
import ComingSoon from "@/pages/workforce/ComingSoon";
import ToolRegistry from "@/pages/workforce/ToolRegistry";
import WorkspaceSettings from "@/pages/workforce/WorkspaceSettings";
import PitchDeckPrint from "@/pages/PitchDeckPrint";
import ModelsPage from "@/pages/Models";
import PromptsPage from "@/pages/Prompts";
import Showcase from "@/pages/Showcase";
import Creators from "@/pages/Creators";
import CreatorProfile from "@/pages/CreatorProfile";
import StudioHub from "@/pages/studio/StudioHub";
import StudioCompose from "@/pages/studio/StudioCompose";
import StudioHFImport from "@/pages/studio/StudioHFImport";
import StudioCustomBrain from "@/pages/studio/StudioCustomBrain";
import StudioFineTune from "@/pages/studio/StudioFineTune";
import Trending from "@/pages/Trending";
import SlackSettings from "@/pages/SlackSettings";
import StudioModel from "@/pages/studio/StudioModel";
import StudioDiff from "@/pages/studio/StudioDiff";
import { BookOpen } from "lucide-react";

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user?.is_admin) return <NotFound />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/share/:id" element={<SharePage />} />
      <Route path="/pitch" element={<AdminOnly><PitchDeck /></AdminOnly>} />
      <Route path="/pitch/pdf" element={<AdminOnly><PitchDeckPrint /></AdminOnly>} />
      <Route path="/models" element={<ModelsPage />} />
      <Route path="/showcase" element={<Showcase />} />
      <Route path="/creators" element={<Creators />} />
      <Route path="/creators/:user_id" element={<CreatorProfile />} />
      <Route path="/trending" element={<Trending />} />
      <Route path="/settings/slack" element={<AppLayout><SlackSettings /></AppLayout>} />
      <Route path="/prompts" element={<AppLayout><PromptsPage /></AppLayout>} />

      {/* Model Studio */}
      <Route path="/studio"                element={<AppLayout><StudioHub /></AppLayout>} />
      <Route path="/studio/new/composed"   element={<AppLayout><StudioCompose /></AppLayout>} />
      <Route path="/studio/new/hf"         element={<AppLayout><StudioHFImport /></AppLayout>} />
      <Route path="/studio/new/custom"     element={<AppLayout><StudioCustomBrain /></AppLayout>} />
      <Route path="/studio/fine-tune"      element={<AppLayout><StudioFineTune /></AppLayout>} />
      <Route path="/studio/fine-tune/:jobId" element={<AppLayout><StudioFineTune /></AppLayout>} />
      <Route path="/studio/diff"           element={<AppLayout><StudioDiff /></AppLayout>} />
      <Route path="/studio/diff/:a/:b"     element={<AppLayout><StudioDiff /></AppLayout>} />
      <Route path="/studio/:id"            element={<AppLayout><StudioModel /></AppLayout>} />

      <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
      <Route path="/chat"      element={<AppLayout><Chat /></AppLayout>} />
      <Route path="/compare"   element={<AppLayout><Compare /></AppLayout>} />
      <Route path="/agents"    element={<AppLayout><Agents /></AppLayout>} />
      <Route path="/agents/run" element={<AppLayout><Agents /></AppLayout>} />
      <Route path="/research"  element={<AppLayout><Research /></AppLayout>} />
      <Route path="/documents" element={<AppLayout><Documents /></AppLayout>} />

      {/* Workforce module */}
      <Route path="/workforce" element={<AppLayout><WorkforceLayout /></AppLayout>}>
        <Route index element={<MyWorkforce />} />
        <Route path="analytics" element={<WorkforceAnalytics />} />
        <Route path="providers" element={<Providers />} />
        <Route path="teams"     element={<Teams />} />
        <Route path="debate"    element={<DebateArena />} />
        <Route path="templates" element={<Templates />} />
        <Route path="workflows" element={<Workflows />} />
        <Route path="runs"      element={<Runs />} />
        <Route path="knowledge" element={<ComingSoon title="Knowledge" phase={2} icon={BookOpen}       blurb="Attach files, URLs, and databases so workers can ground answers in your data." />} />
        <Route path="settings"  element={<WorkspaceSettings />} />
        <Route path="tools"     element={<ToolRegistry />} />
      </Route>
      <Route path="/workforce/workflows/:id" element={<AppLayout><WorkflowEditor /></AppLayout>} />
      <Route path="/workforce/runs/:id" element={<AppLayout><RunDetail /></AppLayout>} />
      <Route path="/workforce/new" element={<AppLayout><CreateWorker /></AppLayout>} />
      <Route path="/workforce/worker/:id" element={<AppLayout><WorkerWorkspace /></AppLayout>} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
